import { useState } from "react";
import {
  Package,
  Leaf,
  ChefHat,
  Plus,
  Minus,
  Star,
  Settings,
  Truck,
} from "lucide-react";
import type { InventoryItem } from "../types/inventoryTypes";
import {
  EditableTextField,
  EditableNumberField,
  EditableSelectField,
} from "./EditableField";
import { EditProductModal } from "./inventaire/EditProductModal";
import { Tooltip } from "./Tooltip";

function RecipeBadge({ item }: { item: InventoryItem }) {
  if (!item.recipeUseCount || item.recipeUseCount === 0) return null;
  const names = item.recipeNames ?? [];
  const content = (
    <div className="space-y-1 max-w-[220px]">
      <div className="font-medium">Utilisée dans {item.recipeUseCount} recette{item.recipeUseCount > 1 ? "s" : ""}</div>
      {names.length > 0 && (
        <div className="space-y-0.5 text-[11px] opacity-90">
          {names.map((n, i) => (
            <div key={i} className="flex items-center gap-1">
              <ChefHat className="w-3 h-3 flex-shrink-0" />
              <span>{n}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <Tooltip content={content} side="top">
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-olive-bg)] text-[var(--color-olive)] border border-[var(--color-olive-border)] cursor-default">
        <ChefHat className="w-3 h-3" strokeWidth={2.25} />
        {item.recipeUseCount}
      </span>
    </Tooltip>
  );
}

function SupplierBadge({ item }: { item: InventoryItem }) {
  if (item.supplierCount === undefined) return null;
  if (item.supplierCount === 0) {
    return (
      <Tooltip content="Aucun fournisseur lié" side="top">
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-[var(--muted)] text-[var(--muted-foreground)] border border-[var(--border)] opacity-60 cursor-default">
          <Truck className="w-3 h-3" strokeWidth={2.25} />
          —
        </span>
      </Tooltip>
    );
  }
  const details = item.supplierDetails ?? [];
  const content = (
    <div className="space-y-1 max-w-[240px]">
      <div className="font-medium">
        {item.supplierCount} fournisseur{item.supplierCount > 1 ? "s" : ""}
      </div>
      {details.length > 0 && (
        <div className="space-y-0.5 text-[11px]">
          {details.map((s, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1">
                {s.isPreferred && <Star className="w-2.5 h-2.5 fill-current text-amber-400" />}
                <span>{s.name}</span>
              </span>
              {s.unitCost != null && (
                <span className="tabular-nums opacity-80">{s.unitCost.toFixed(2)} $</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <Tooltip content={content} side="top">
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-steel-bg)] text-[var(--color-steel)] border border-[var(--color-steel-border)] cursor-default">
        <Truck className="w-3 h-3" strokeWidth={2.25} />
        {item.supplierCount}
      </span>
    </Tooltip>
  );
}

// Helper function to format volume display
function formatVolume(item: InventoryItem): string | null {
  if (!item.formatQuantite || item.formatQuantite <= 0) return null;

  const unit = item.formatUnite || "L";
  if (item.formatQuantite >= 1000) {
    return `${(item.formatQuantite / 1000).toFixed(1)}L`;
  }
  return `${item.formatQuantite}${unit === "L" ? "ml" : unit}`;
}

interface InventoryDenseRowProps {
  item: InventoryItem;
  isSelected: boolean;
  onToggleSelection: (id: string) => void;
  onQuantityChange: (id: string, newQuantity: number) => void;
  onFieldUpdate: (id: string, field: keyof InventoryItem, value: any) => void;
  onImageEdit: (id: string, currentImage: string, itemName: string) => void;
  categories: readonly string[];
  lowStockThreshold: number;
  selectionMode?: boolean;
}

export function InventoryDenseRow({
  item,
  isSelected,
  onToggleSelection,
  onQuantityChange,
  onFieldUpdate,
  onImageEdit,
  categories,
  lowStockThreshold,
  selectionMode = false,
}: InventoryDenseRowProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Utiliser les seuils personnalisés ou les valeurs par défaut
  const seuilStock = item.seuilStock ?? lowStockThreshold;
  const seuilRupture = item.seuilRupture ?? 0;

  const isLowStock = item.quantite <= seuilStock && item.quantite > seuilRupture;
  const isOutOfStock = item.quantite <= seuilRupture;

  const adjustQuantity = (delta: number) => {
    const newQuantity = Math.max(0, Math.round(item.quantite + delta));
    onQuantityChange(item.id, newQuantity);
  };

  // Get quantity text color based on stock status
  const getQuantityColor = () => {
    if (isOutOfStock) return "text-[var(--color-brick)]";
    if (isLowStock) return "text-[var(--color-amber)]";
    return "text-[var(--foreground)]";
  };

  // Get type icon and label - NO COLORS, only grey icons
  const getTypeConfig = () => {
    switch (item.typeItem) {
      case "produit_simple":
        return {
          icon: <Package className="w-3.5 h-3.5" />,
          label: "Produit simple",
        };
      case "ingredient":
        return {
          icon: <Leaf className="w-3.5 h-3.5" />,
          label: "Ingrédient",
        };
      case "recette":
        return {
          icon: <ChefHat className="w-3.5 h-3.5" />,
          label: "Recette",
        };
      default:
        return {
          icon: <Package className="w-3.5 h-3.5" />,
          label: "Produit simple",
        };
    }
  };

  const typeConfig = getTypeConfig();

  // Format volume display
  const volumeDisplay = formatVolume(item);
  
  // Display quantity (could be adjusted later for unit conversions)
  const displayQuantity = item.quantite;

  // Handle row click for selection mode
  const handleRowClick = (e: React.MouseEvent) => {
    if (!selectionMode) return;
    
    // Don't trigger selection if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'BUTTON' ||
      target.tagName === 'INPUT' ||
      target.tagName === 'SELECT' ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('select')
    ) {
      return;
    }
    
    onToggleSelection(item.id);
  };

  return (
    <>
      {/* Mobile: Vertical Card - COMPACT */}
      <div
        className={`md:hidden bg-[var(--card)] rounded-lg border-2 ${
          isSelected
            ? "border-[var(--color-steel)] bg-[var(--color-steel)]/10 shadow-lg shadow-[var(--color-steel)]/20"
            : selectionMode
            ? "border-dashed border-[var(--color-steel)]/50 hover:border-[var(--color-steel)] hover:bg-[var(--color-steel)]/5"
            : "border-[var(--border)]"
        } overflow-hidden transition-all relative`}
        onClick={handleRowClick}
      >
        {/* Delivery Badge - Top Right Corner */}
        {item.deliveryInfo && (
          <div
            className="absolute top-2 right-2 bg-gradient-to-b from-[var(--color-amber)] to-[var(--color-amber-dark)] text-white shadow-lg px-2 py-2 flex flex-col items-center gap-0.5 rounded-md z-10"
            style={{
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4), 0 0 8px var(--color-amber)"
            }}
            title={`Livraison: ${item.deliveryInfo.quantity} unités`}
          >
            <Truck className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold">{item.deliveryInfo.quantity}</span>
          </div>
        )}

        <div className="p-2.5 space-y-2">
          {/* Header: Image + Name + Type */}
          <div className="flex gap-2.5">
            <div
              className={`relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-[var(--background-subtle)] to-[var(--muted)] overflow-hidden ${
                selectionMode ? "cursor-pointer" : ""
              } rounded-lg ${
                isSelected ? "ring-4 ring-[var(--color-steel)]" : ""
              }`}
              onClick={() => selectionMode && onToggleSelection(item.id)}
            >
              <img
                src={item.image}
                alt={item.nom}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
              {/* Product Name */}
              <EditableTextField
                value={item.nom}
                onSave={(value) => onFieldUpdate(item.id, "nom", value)}
                displayClassName="font-bold text-[var(--foreground)] text-lg leading-tight line-clamp-2"
              />
              {(item.recipeUseCount !== undefined || item.supplierCount !== undefined) && (
                <div className="flex items-center gap-1 mt-0.5">
                  <RecipeBadge item={item} />
                  <SupplierBadge item={item} />
                </div>
              )}
            </div>
          </div>

          {/* Quantity + Actions - Single Row */}
          <div className="flex items-center gap-2">
            {/* Quantity Controls - Neutral background, color only on number */}
            <div className="flex-1 flex items-center justify-center gap-2 rounded-lg p-1.5 border bg-gradient-to-br from-[var(--color-steel)]/15 to-[var(--color-steel-dark)]/10 border-[var(--color-steel)]/20">
              <button
                onClick={() => adjustQuantity(-1)}
                className="p-1.5 hover:bg-[var(--color-brick)]/20 hover:text-[var(--color-brick)] rounded-lg transition-all min-h-[40px] min-w-[40px] flex items-center justify-center group/btn"
                disabled={displayQuantity === 0}
              >
                <Minus className="w-4 h-4 text-[var(--foreground)] group-hover/btn:text-[var(--color-brick)]" />
              </button>
              <div className="min-w-[3rem] flex items-center justify-center">
                <EditableNumberField
                  value={displayQuantity}
                  onSave={(value) => onQuantityChange(item.id, Math.round(value))}
                  displayClassName={`text-center font-bold text-xl ${getQuantityColor()}`}
                  prefix=""
                  decimals={0}
                />
              </div>
              <button
                onClick={() => adjustQuantity(1)}
                className="p-1.5 hover:bg-[var(--color-olive)]/20 hover:text-[var(--color-olive)] rounded-lg transition-all min-h-[40px] min-w-[40px] flex items-center justify-center group/btn"
              >
                <Plus className="w-4 h-4 text-[var(--foreground)] group-hover/btn:text-[var(--color-olive)]" />
              </button>
            </div>

            {/* Actions - Same Row */}
            <button
              onClick={() => onFieldUpdate(item.id, "inCart", !item.inCart)}
              className={`p-2 rounded-lg transition-all min-h-[40px] min-w-[40px] flex items-center justify-center ${
                item.inCart
                  ? "bg-gradient-to-br from-[var(--color-steel)] to-[var(--color-steel-dark)] text-white shadow-lg shadow-[var(--color-steel)]/30 hover:from-[var(--color-steel-light)] hover:to-[var(--color-steel)]"
                  : "bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--color-steel)]/10 hover:text-[var(--color-steel)]"
              }`}
              title={item.inCart ? "Retirer du rappel" : "Ajouter au rappel"}
            >
              <Star className={`w-4 h-4 ${item.inCart ? "fill-current" : ""}`} />
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="p-2 bg-[var(--muted)] hover:bg-gradient-to-br hover:from-[var(--color-steel)]/10 hover:to-[var(--color-steel-dark)]/10 text-[var(--muted-foreground)] hover:text-[var(--color-steel)] rounded-lg transition-all min-h-[40px] min-w-[40px] flex items-center justify-center"
              title="Paramètres"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Edit Product Modal */}
      <EditProductModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        item={item}
        onFieldUpdate={onFieldUpdate}
      />

      {/* Desktop/Tablet: Horizontal Row - COMPACT & UNIFORM HEIGHT */}
      <div
        className={`hidden md:flex items-center gap-0 bg-[var(--card)] rounded-lg border-2 ${
          isSelected
            ? "border-[var(--color-steel)] bg-[var(--color-steel)]/10 shadow-lg shadow-[var(--color-steel)]/20"
            : selectionMode
            ? "border-dashed border-[var(--color-steel)]/50 hover:border-[var(--color-steel)] hover:bg-[var(--color-steel)]/5 cursor-pointer"
            : "border-[var(--border)] hover:border-[var(--color-steel)]/40 hover:shadow-md"
        } overflow-hidden transition-all group h-[72px]`}
        onClick={handleRowClick}
      >
        {/* Image - Square, Full Height */}
        <div
          className={`relative w-[72px] h-[72px] flex-shrink-0 bg-gradient-to-br from-[var(--background-subtle)] to-[var(--muted)] ${
            selectionMode ? "cursor-pointer" : ""
          } ${
            isSelected ? "ring-4 ring-inset ring-[var(--color-steel)]" : ""
          }`}
          onClick={() => selectionMode && onToggleSelection(item.id)}
        >
          <img
            src={item.image}
            alt={item.nom}
            className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
          />
          
          {/* Delivery Badge - Ribbon Style */}
          {item.deliveryInfo && (
            <div
              className="absolute top-0 right-2 bg-gradient-to-b from-[var(--color-amber)] to-[var(--color-amber-dark)] text-white shadow-lg px-2 py-3 flex flex-col items-center gap-0.5 rounded-b-md"
              style={{
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4), 0 0 8px var(--color-amber)"
              }}
              title={`Livraison: ${item.deliveryInfo.quantity} unités le ${item.deliveryInfo.estimatedDate}`}
            >
              <Truck className="w-4 h-4" />
              <span className="text-xs font-bold">{item.deliveryInfo.quantity}</span>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 flex items-center gap-3 px-3 min-w-0">
          {/* Name + Meta Column */}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            {/* Product Name - LARGER, Primary Focus */}
            <div className="w-fit max-w-full">
              <EditableTextField
                value={item.nom}
                onSave={(value) => onFieldUpdate(item.id, "nom", value)}
                displayClassName="font-bold text-[var(--foreground)] text-lg leading-tight line-clamp-1 hover:text-[var(--color-steel)] cursor-text transition-colors"
              />
            </div>
            
            {/* Type + Category + Volume - Single unified row with left margin */}
            <div className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] ml-0.5">
              <div className="flex items-center gap-1.5 font-medium">
                {typeConfig.icon}
                <EditableSelectField
                  value={item.typeItem}
                  onSave={(value) => onFieldUpdate(item.id, "typeItem", value)}
                  options={["produit_simple", "ingredient", "recette"]}
                  displayClassName="text-[var(--muted-foreground)] font-medium hover:text-[var(--foreground)] cursor-pointer transition-colors"
                  renderDisplay={(value) => typeConfig.label}
                  renderOption={(value) => {
                    switch (value) {
                      case "produit_simple": return "Produit simple";
                      case "ingredient": return "Ingrédient";
                      case "recette": return "Recette";
                      default: return value;
                    }
                  }}
                />
              </div>
              <span>•</span>
              <EditableSelectField
                value={item.categorie}
                onSave={(value) => onFieldUpdate(item.id, "categorie", value)}
                options={[...categories]}
                displayClassName="text-[var(--color-steel)] font-medium hover:text-[var(--color-steel-light)] cursor-pointer transition-colors"
              />
              {volumeDisplay && (
                <>
                  <span>•</span>
                  <span className="text-[var(--color-steel)] font-semibold">{volumeDisplay}</span>
                </>
              )}
              <RecipeBadge item={item} />
              <SupplierBadge item={item} />
            </div>
          </div>

          {/* Quantity Controls - Neutral background, color only on number */}
          <div className="flex-shrink-0 flex items-center gap-3 rounded-lg px-3 py-2 border bg-gradient-to-br from-[var(--color-steel)]/15 to-[var(--color-steel-dark)]/10 border-[var(--color-steel)]/20">
            <button
              onClick={() => adjustQuantity(-1)}
              className="p-1.5 bg-gradient-to-br from-white/10 to-white/5 hover:from-[var(--color-brick)]/15 hover:to-[var(--color-brick)]/25 hover:text-[var(--color-brick)] rounded transition-all min-h-[36px] min-w-[36px] flex items-center justify-center group/btn border border-white/10"
              disabled={displayQuantity === 0}
            >
              <Minus className="w-4 h-4 text-[var(--foreground)] group-hover/btn:text-[var(--color-brick)]" />
            </button>
            <div className="min-w-[3rem] flex items-center justify-center">
              <EditableNumberField
                value={displayQuantity}
                onSave={(value) => onQuantityChange(item.id, Math.round(value))}
                displayClassName={`text-center font-bold text-xl ${getQuantityColor()}`}
                prefix=""
                decimals={0}
              />
            </div>
            <button
              onClick={() => adjustQuantity(1)}
              className="p-1.5 bg-gradient-to-br from-white/10 to-white/5 hover:from-[var(--color-olive)]/15 hover:to-[var(--color-olive)]/25 hover:text-[var(--color-olive)] rounded transition-all min-h-[36px] min-w-[36px] flex items-center justify-center group/btn border border-white/10"
            >
              <Plus className="w-4 h-4 text-[var(--foreground)] group-hover/btn:text-[var(--color-olive)]" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex-shrink-0 flex items-center gap-2">
            <button
              onClick={() => onFieldUpdate(item.id, "inCart", !item.inCart)}
              className={`p-2 rounded-lg transition-all min-h-[36px] min-w-[36px] flex items-center justify-center ${
                item.inCart
                  ? "bg-gradient-to-br from-[var(--color-steel)] to-[var(--color-steel-dark)] text-white shadow-lg shadow-[var(--color-steel)]/30 hover:from-[var(--color-steel-light)] hover:to-[var(--color-steel)]"
                  : "bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--color-steel)]/10 hover:text-[var(--color-steel)]"
              }`}
              title={item.inCart ? "Retirer du rappel" : "Ajouter au rappel"}
            >
              <Star className={`w-4 h-4 ${item.inCart ? "fill-current" : ""}`} />
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="p-2 bg-[var(--muted)] hover:bg-gradient-to-br hover:from-[var(--color-steel)]/10 hover:to-[var(--color-steel-dark)]/10 text-[var(--muted-foreground)] hover:text-[var(--color-steel)] rounded-lg transition-all min-h-[36px] min-w-[36px] flex items-center justify-center"
              title="Paramètres"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}